import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

/**
 * Service pour gérer la configuration des produits Stripe dans Firestore
 */
class StripeProductsConfigService {
  constructor() {
    this.configCollection = 'stripeProductsConfig';
    this.configDocId = 'productsUsage';
  }

  /**
   * Récupère la configuration des produits
   */
  async getProductsConfig() {
    try {
      const configDoc = await getDoc(doc(db, this.configCollection, this.configDocId));
      
      if (configDoc.exists()) {
        return configDoc.data();
      } else {
        // Configuration par défaut
        return {
          subscriptionProducts: [],
          invoiceProducts: [],
          lastUpdated: new Date()
        };
      }
    } catch (error) {
      console.error('❌ Erreur lors de la récupération de la configuration:', error);
      throw error;
    }
  }

  /**
   * Sauvegarde la configuration des produits
   */
  async saveProductsConfig(config) {
    try {
      await setDoc(doc(db, this.configCollection, this.configDocId), {
        ...config,
        lastUpdated: new Date()
      });
      console.log('✅ Configuration des produits sauvegardée avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de la configuration:', error);
      throw error;
    }
  }

  /**
   * Récupère les IDs des produits configurés pour les abonnements
   */
  async getSubscriptionProductIds() {
    try {
      const config = await this.getProductsConfig();
      return config.subscriptionProducts || [];
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des produits abonnement:', error);
      return [];
    }
  }

  /**
   * Récupère les IDs des produits configurés pour la facturation
   */
  async getInvoiceProductIds() {
    try {
      const config = await this.getProductsConfig();
      return config.invoiceProducts || [];
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des produits facturation:', error);
      return [];
    }
  }

  /**
   * Vérifie si un produit est configuré pour les abonnements
   */
  async isProductConfiguredForSubscription(productId) {
    const subscriptionIds = await this.getSubscriptionProductIds();
    return subscriptionIds.includes(productId);
  }

  /**
   * Vérifie si un produit est configuré pour la facturation
   */
  async isProductConfiguredForInvoice(productId) {
    const invoiceIds = await this.getInvoiceProductIds();
    return invoiceIds.includes(productId);
  }

  /**
   * Ajoute un produit à la configuration des abonnements
   */
  async addProductToSubscription(productId) {
    try {
      const config = await this.getProductsConfig();
      const subscriptionProducts = config.subscriptionProducts || [];
      
      if (!subscriptionProducts.includes(productId)) {
        subscriptionProducts.push(productId);
        await this.saveProductsConfig({
          ...config,
          subscriptionProducts
        });
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout du produit aux abonnements:', error);
      throw error;
    }
  }

  /**
   * Supprime un produit de la configuration des abonnements
   */
  async removeProductFromSubscription(productId) {
    try {
      const config = await this.getProductsConfig();
      const subscriptionProducts = (config.subscriptionProducts || []).filter(id => id !== productId);
      
      await this.saveProductsConfig({
        ...config,
        subscriptionProducts
      });
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du produit des abonnements:', error);
      throw error;
    }
  }

  /**
   * Ajoute un produit à la configuration de facturation
   */
  async addProductToInvoice(productId) {
    try {
      const config = await this.getProductsConfig();
      const invoiceProducts = config.invoiceProducts || [];
      
      if (!invoiceProducts.includes(productId)) {
        invoiceProducts.push(productId);
        await this.saveProductsConfig({
          ...config,
          invoiceProducts
        });
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout du produit à la facturation:', error);
      throw error;
    }
  }

  /**
   * Supprime un produit de la configuration de facturation
   */
  async removeProductFromInvoice(productId) {
    try {
      const config = await this.getProductsConfig();
      const invoiceProducts = (config.invoiceProducts || []).filter(id => id !== productId);
      
      await this.saveProductsConfig({
        ...config,
        invoiceProducts
      });
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du produit de la facturation:', error);
      throw error;
    }
  }

  /**
   * Synchronise automatiquement la configuration avec les produits Stripe existants
   * Ajoute les produits qui correspondent aux métadonnées mais ne sont pas encore configurés
   */
  async autoSyncConfiguration() {
    try {
      console.log('🔄 Synchronisation automatique de la configuration...');
      
      // Import dynamique pour éviter les dépendances circulaires
      const stripeSyncService = await import('./stripeSyncService.js');
      const products = await stripeSyncService.default.getProductsWithCache();
      
      const config = await this.getProductsConfig();
      const subscriptionProducts = [...(config.subscriptionProducts || [])];
      const invoiceProducts = [...(config.invoiceProducts || [])];
      
      let addedSubscriptionProducts = 0;
      let addedInvoiceProducts = 0;
      
      // Parcourir tous les produits Stripe
      for (const product of products) {
        const productId = product.id;
        
        // Vérifier les produits pour abonnements
        if (product.metadata?.category === 'subscription' && 
            product.metadata?.target === 'employee' &&
            product.active &&
            !subscriptionProducts.includes(productId)) {
          subscriptionProducts.push(productId);
          addedSubscriptionProducts++;
          console.log(`✅ Produit ajouté aux abonnements: ${product.name} (${productId})`);
        }
        
        // Vérifier les produits pour facturation
        if (product.metadata?.category === 'billing' && 
            product.metadata?.target === 'beneficiary' &&
            product.active &&
            !invoiceProducts.includes(productId)) {
          invoiceProducts.push(productId);
          addedInvoiceProducts++;
          console.log(`✅ Produit ajouté à la facturation: ${product.name} (${productId})`);
        }
      }
      
      // Sauvegarder la configuration mise à jour si des changements ont été détectés
      if (addedSubscriptionProducts > 0 || addedInvoiceProducts > 0) {
        await this.saveProductsConfig({
          ...config,
          subscriptionProducts,
          invoiceProducts
        });
        
        console.log(`✅ Synchronisation terminée: ${addedSubscriptionProducts} produits abonnement, ${addedInvoiceProducts} produits facturation ajoutés`);
      } else {
        console.log('✅ Aucun nouveau produit à synchroniser');
      }
      
      return {
        addedSubscriptionProducts,
        addedInvoiceProducts,
        totalSubscriptionProducts: subscriptionProducts.length,
        totalInvoiceProducts: invoiceProducts.length
      };
    } catch (error) {
      console.error('❌ Erreur lors de la synchronisation automatique:', error);
      throw error;
    }
  }

  /**
   * Nettoie la configuration en supprimant les produits qui n'existent plus dans Stripe
   */
  async cleanConfiguration() {
    try {
      console.log('🧹 Nettoyage de la configuration...');
      
      // Import dynamique pour éviter les dépendances circulaires
      const stripeSyncService = await import('./stripeSyncService.js');
      const products = await stripeSyncService.default.getProductsWithCache();
      const productIds = products.map(p => p.id);
      
      const config = await this.getProductsConfig();
      const subscriptionProducts = (config.subscriptionProducts || []).filter(id => 
        productIds.includes(id)
      );
      const invoiceProducts = (config.invoiceProducts || []).filter(id => 
        productIds.includes(id)
      );
      
      const removedSubscriptionProducts = (config.subscriptionProducts || []).length - subscriptionProducts.length;
      const removedInvoiceProducts = (config.invoiceProducts || []).length - invoiceProducts.length;
      
      // Sauvegarder la configuration nettoyée si des changements ont été détectés
      if (removedSubscriptionProducts > 0 || removedInvoiceProducts > 0) {
        await this.saveProductsConfig({
          ...config,
          subscriptionProducts,
          invoiceProducts
        });
        
        console.log(`✅ Nettoyage terminé: ${removedSubscriptionProducts} produits abonnement, ${removedInvoiceProducts} produits facturation supprimés`);
      } else {
        console.log('✅ Aucun produit obsolète à nettoyer');
      }
      
      return {
        removedSubscriptionProducts,
        removedInvoiceProducts,
        totalSubscriptionProducts: subscriptionProducts.length,
        totalInvoiceProducts: invoiceProducts.length
      };
    } catch (error) {
      console.error('❌ Erreur lors du nettoyage de la configuration:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques de configuration avec diagnostic
   */
  async getConfigStats() {
    try {
      const config = await this.getProductsConfig();
      
      // Import dynamique pour éviter les dépendances circulaires
      const stripeSyncService = await import('./stripeSyncService.js');
      const products = await stripeSyncService.default.getProductsWithCache();
      const productIds = products.map(p => p.id);
      
      // Identifier les produits configurés mais non trouvés dans Stripe
      const missingSubscriptionProducts = (config.subscriptionProducts || []).filter(id => 
        !productIds.includes(id)
      );
      const missingInvoiceProducts = (config.invoiceProducts || []).filter(id => 
        !productIds.includes(id)
      );
      
      return {
        subscriptionProductsCount: (config.subscriptionProducts || []).length,
        invoiceProductsCount: (config.invoiceProducts || []).length,
        missingSubscriptionProductsCount: missingSubscriptionProducts.length,
        missingInvoiceProductsCount: missingInvoiceProducts.length,
        missingSubscriptionProducts,
        missingInvoiceProducts,
        lastUpdated: config.lastUpdated
      };
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des statistiques:', error);
      return {
        subscriptionProductsCount: 0,
        invoiceProductsCount: 0,
        missingSubscriptionProductsCount: 0,
        missingInvoiceProductsCount: 0,
        missingSubscriptionProducts: [],
        missingInvoiceProducts: [],
        lastUpdated: null
      };
    }
  }
}

// Instance singleton
const stripeProductsConfigService = new StripeProductsConfigService();

export default stripeProductsConfigService;
